import type { ReactElement } from 'react';
import { type QuoteStatusFilter, useQuotesListQuery } from '@/entities/quote';
import { cn, EmptyState, Pagination, tableStyles } from '@/shared/ui';
import { QuoteRow } from './quote-row';

export interface QuoteListProps {
  readonly page: number;
  readonly statusFilter: QuoteStatusFilter;
  /** 적용된 검색어(견적 번호·회사·단체명·담당자명). */
  readonly search: string;
  readonly onPageChange: (page: number) => void;
  readonly onOpen: (quoteRequestId: string) => void;
}

const COLUMNS = [
  { label: '견적 번호' },
  { label: '요청일' },
  { label: '회사·담당자' },
  { label: '상품·수량' },
  { label: '희망 납기일' },
  { label: '상태' },
  { label: '최신 발행본 합계', className: 'text-right' },
  { label: '경고' },
] as const;

/**
 * 어드민 견적 목록 표(견적 설계 §6-1) — 조회·경고 배지·페이지 이동을 묶은 자립 블록. 주문 목록과 같은 흰 표 카드이고,
 * 읽는 중·실패·빈 결과는 카드 안 가운데 안내(EmptyState)로 보인다.
 */
export const QuoteList = ({
  page,
  statusFilter,
  search,
  onPageChange,
  onOpen,
}: QuoteListProps): ReactElement => {
  const quotesQuery = useQuotesListQuery({ page, statusFilter, search });

  if (quotesQuery.isPending) {
    return (
      <div className="mt-5">
        <EmptyState>불러오는 중…</EmptyState>
      </div>
    );
  }

  if (quotesQuery.isError) {
    return (
      <div className="mt-5">
        <EmptyState isError>목록을 불러오지 못했습니다: {quotesQuery.error.message}</EmptyState>
      </div>
    );
  }

  if (quotesQuery.data.items.length === 0) {
    return (
      <div className="mt-5">
        <EmptyState>조건에 맞는 견적이 없습니다.</EmptyState>
      </div>
    );
  }

  return (
    <>
      <div className={cn('mt-5', tableStyles.card)}>
        <table className={tableStyles.table}>
          <thead className={tableStyles.head}>
            <tr>
              {COLUMNS.map((column) => (
                <th
                  key={column.label}
                  className={cn(tableStyles.th, 'className' in column && column.className)}
                >
                  {column.label}
                </th>
              ))}
              <th className={tableStyles.th}>
                <span className="sr-only">상세</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {quotesQuery.data.items.map((quote) => (
              <QuoteRow key={quote.id} quote={quote} onOpen={onOpen} />
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        className="mt-5 justify-center"
        page={quotesQuery.data.meta.page}
        totalPages={quotesQuery.data.meta.totalPages}
        onPageChange={onPageChange}
      />
    </>
  );
};
